import { FC, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useClickOutside } from "../../../hooks/useClickOutside";
import { LanguageToggle } from "../LanguageToggle";
import { LANDING_NAV_TABS, LANDING_DONATE_ITEM, LANDING_CONTACT_ITEM } from "../navConfig";
import { useTranslation } from "../../../hooks/useTranslation";

interface IProps {
  open: boolean;
  onClose: () => void;
  locale: string;
  onLanguageChange: (language: string) => void;
}

// Mobile nav panel shown below the 900px breakpoint. Every item's onClick
// closes the menu directly (co-located with the click) in addition to the
// reused useClickOutside hook and an Escape keydown listener, so the menu
// reliably closes on navigate / outside click / Escape.
export const MobileMenu: FC<IProps> = ({ open, onClose, locale, onLanguageChange }) => {
  const { p } = useTranslation("landing_nav");
  const location = useLocation();
  const panelRef = useClickOutside({ handler: onClose });

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  const handleItemClick = () => {
    onClose();
  };

  return (
    <div
      className={"landing-mobile-menu" + (open ? " landing-mobile-menu--open" : "")}
      aria-hidden={!open}
    >
      <div className="landing-mobile-menu__backdrop" onClick={onClose} />
      <div className="landing-mobile-menu__panel" ref={panelRef}>
        {LANDING_NAV_TABS.map((tab) => (
          <Link
            key={tab.key}
            to={tab.path}
            className={
              "landing-mobile-menu__tab" +
              (location.pathname === tab.path ? " landing-mobile-menu__tab--active" : "")
            }
            onClick={handleItemClick}
          >
            {p(tab.key)}
          </Link>
        ))}

        <div className="landing-mobile-menu__divider" />

        <Link
          to={LANDING_DONATE_ITEM.path}
          className="landing-mobile-menu__link"
          onClick={handleItemClick}
        >
          {p("donate")}
        </Link>
        <Link
          to={LANDING_CONTACT_ITEM.path}
          className="landing-mobile-menu__link"
          onClick={handleItemClick}
        >
          {p("contact_us")}
        </Link>
        <Link to="/login" className="landing-mobile-menu__link" onClick={handleItemClick}>
          {p("log_in")}
        </Link>
        <Link to="/welcome" className="landing-mobile-menu__cta" onClick={handleItemClick}>
          {p("get_started")}
        </Link>

        <div className="landing-mobile-menu__lang-row">
          <LanguageToggle language={locale} onChange={onLanguageChange} />
        </div>
      </div>
    </div>
  );
};
